import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnDestroy,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../../utils/observable-utils';
import {PaymentModel} from "../../payment.model";
import {
    IntegratedPaymentProvider,
    PaymentAuthorisationType,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult
} from '../../payment';
import {Color} from "../../../ui-elements/color";
import {SpinnerComponent} from "../../../ui-elements/spinner/spinner.component";
import {TyroPayRequestError} from "../../tyro";
import {CssVarDirective} from '../../../utils/css-var.directive';
import {ConfigurationModel} from '../../../configuration/configuration.model';
import {ActivatableDirective} from '../../../ui-elements/activatable.directive';

@Component({
    selector: "woa-apple-pay-button",
    templateUrl: "./apple-pay-button.component.html",
    styleUrl: "./apple-pay-button.component.scss",
    host: {
        "[class.disabled]": "disabled()",
        "[class.woa-button]": "true"
    },
    imports: [
        CssVarDirective,
        SpinnerComponent,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplePayButtonComponent implements AfterViewInit, OnDestroy {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private element: ElementRef = inject(ElementRef);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public paymentModel: PaymentModel = inject(PaymentModel);

    IntegratedPaymentProvider = IntegratedPaymentProvider;

    public readonly type: InputSignal<"plain" | "check-out"> = input.required<"plain" | "check-out">();

    public readonly passiveOnly: InputSignal<boolean> = input<boolean>(false);
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly showSpinner: InputSignal<boolean> = input<boolean>(false);

    public pay: OutputEmitterRef<void> = output<void>();
    public paymentStarted: OutputEmitterRef<void> = output<void>();
    public paymentComplete: OutputEmitterRef<WalletPaymentSubmissionResult> = output<WalletPaymentSubmissionResult>();
    public paymentFailed: OutputEmitterRef<WalletPaymentSubmissionFailure> = output<WalletPaymentSubmissionFailure>();
    public paymentCancelled: OutputEmitterRef<void> = output<void>();

    public readonly style: WritableSignal<"black" | "white"> = signal<"black" | "white">(undefined);
    private readonly payFormReady: WritableSignal<boolean> = signal<boolean>(false);
    private readonly payFormError: WritableSignal<string> = signal<string>(undefined);

    public readonly isPassive: Signal<boolean> = computed<boolean>(() =>
        this.passiveOnly() || this.paymentModel.paymentProvider() !== IntegratedPaymentProvider.TYRO);

    public readonly showTyroSpinner: Signal<boolean> = computed<boolean>(() =>
        !this.isPassive() && (!this.paymentModel.paymentPlatformPublicKey() || (!this.payFormReady() && !this.payFormError())));

    private tyro: any;
    private payForm: any;
    private tyroPayFormHolder: Element;
    private paymentPlatformPublicKey: string;
    private destroyed: boolean = false;

    constructor() {
        effect(() => {
            if (this.configurationModel.app()) {
                this.determineColor();
            }
        });

        effect(() => this.enableOrDisableTyroPayForm());
    }

    async ngAfterViewInit(): Promise<void> {
        onEach(this.paymentModel.paymentDetailsApplied$, async () => this.setupTyroPayForm(), this.destroyRef);
        return this.setupTyroPayForm();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
    }

    public activate(): void {
        if (!this.disabled()) {
            this.pay.emit();
        }
    }

    private async setupTyroPayForm(): Promise<void> {
        if (this.isPassive() || this.paymentModel.paymentProvider() !== IntegratedPaymentProvider.TYRO || this.destroyed) {
            return;
        }

        this.enableOrDisableTyroPayForm();

        // Tyro uses the following test to check that it can inject its form - we can't use a ViewChild, because it needs to be in the HTML DOM.
        this.tyroPayFormHolder = document.querySelector("#tyro-apple-pay-form-holder");
        if (!this.tyroPayFormHolder) {
            setTimeout(async () => await this.setupTyroPayForm(), 500);
            return;
        }

        // The tyro secret (paymentPlatformPublicKey) changes if the order is re-initiated - the tyro form needs to be rebuilt.
        if (!this.paymentModel.paymentPlatformPublicKey() || this.paymentPlatformPublicKey === this.paymentModel.paymentPlatformPublicKey()) {
            return;
        }
        this.paymentPlatformPublicKey = this.paymentModel.paymentPlatformPublicKey();

        try {
            console.log("Initialising Tyro for apple pay button. Live Mode = ", this.paymentModel.paymentPlatformLiveMode());
            this.tyro = (<any>window).Tyro({
                liveMode: this.paymentModel.paymentPlatformLiveMode()
            });
            await this.tyro.init(this.paymentPlatformPublicKey);

            this.payForm = this.tyro.createPayForm({
                options: {
                    applePay: {
                        enabled: true
                    },
                    creditCardForm: {
                        enabled: false
                    },
                    walletButtons: {
                        singleButtonOnly: true
                    }
                },
                styleProps: {
                    applePayButton: {
                        buttonStyle: this.style(),
                        buttonType: this.type(),
                        buttonBorderRadius: "5px"
                    },
                    bodyMinWidth: 290,
                    bodyMaxWidth: 980,
                    labelPosition: "block",
                    walletPaymentsButtonsMargin: "0",
                    walletPaymentsButtonsHeight: "100%",
                    walletPaymentsButtonsWidth: "100%"
                }
            });
            if (!this.payForm.isApplePaySupported()) {
                console.log("Tyro pay form does not support apple pay");
                this.payFormError.set("Tyro pay form does not support apple pay");
                return;
            }
            if ((<HTMLElement>this.tyroPayFormHolder).firstChild) {
                this.tyroPayFormHolder.removeChild((<HTMLElement>this.tyroPayFormHolder).firstChild);
            }
            await this.payForm.inject("#tyro-apple-pay-form-holder");
            this.payForm.setReadyListener(() => {
                this.enableOrDisableTyroPayForm();
                this.payFormReady.set(true);
            });

            // noinspection JSUnusedLocalSymbols
            this.payForm.setWalletPaymentBeginListener((paymentType: string) => {
                if (this.disabled()) {
                    throw "Apple pay button disabled";
                }
                this.paymentStarted.emit();
            });

            // noinspection JSUnusedLocalSymbols,DuplicatedCode
            this.payForm.setWalletPaymentCompleteListener(async (paymentType: string, error: TyroPayRequestError) => {
                if (error) {
                    this.paymentFailed.emit(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.type, error.errorMessage, error.errorCode, error.gatewayCode));
                } else {
                    this.paymentComplete.emit(new WalletPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT));
                }
            });

            // noinspection JSUnusedLocalSymbols
            this.payForm.setWalletPaymentCancelledListener((paymentType: string) => this.paymentCancelled.emit());
        } catch (error) {
            console.log("Failed to create Tyro pay form for Apple Pay button", error);
            this.payFormError.set(error.message.replace(/\.$/, ""));
        }
    }

    private determineColor(): void {
        const bg: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--theme-color-pair-background").trim();
        if (bg) {
            const rgb: string[] = bg.split(" ");
            const bgColor: Color = Color.fromRGB(parseInt(rgb[0]), parseInt(rgb[1]), parseInt(rgb[2]));
            this.style.set(bgColor.getContrastRatio(Color.BLACK) > bgColor.getContrastRatio(Color.WHITE) ? "black" : "white");
        }
    }

    private enableOrDisableTyroPayForm(): void {
        if (this.tyroPayFormHolder && (<HTMLElement>this.tyroPayFormHolder).firstChild) {
            (<HTMLElement>(<HTMLElement>this.tyroPayFormHolder).firstChild).tabIndex = this.disabled() ? -1 : undefined;
        }
    }
}
